import React, { useEffect, useRef, useState } from "react";
import { message, Modal, Button } from "antd";
import EnhancedTable from "@/hocComponents/EnhancedTable";
import http from "@/utils/http";
import api from "@/utils/api";
import { buildRequestParams, formatPlainDate } from "./utils";

const CLEARED_TRANS_TYPE_OPTIONS = [
  { value: "Book Trans", label: "Book Trans" },
  { value: "Bank Trans", label: "Bank Trans" },
];
const JOURNAL_OPTIONS = [
  { value: "CP", label: "CP" },
  { value: "CR", label: "CR" },
];

const columns = [
  { title: "Trans Type", dataIndex: "type", filterKey: "type", filterType: "select", selectOptions: CLEARED_TRANS_TYPE_OPTIONS },
  { title: "Match Id", dataIndex: "matchId", filterKey: "matchId", filterType: "input" },
  { title: "Cleared Date", dataIndex: "clearedDate", filterKey: "clearedDate", filterType: "date", render: formatPlainDate },
  { title: "Trans Date", dataIndex: "date1", filterKey: "date1", filterType: "date", render: formatPlainDate },
  { title: "Check #", dataIndex: "checkNo", filterKey: "checkNo", filterType: "input" },
  { title: "Batch", dataIndex: "batch", filterKey: "batch", filterType: "input" },
  { title: "Prop", dataIndex: "prop", filterKey: "prop", filterType: "input" },
  { title: "Amount", dataIndex: "amountRow", filterKey: "amount", filterType: "input" },
  { title: "Journal", dataIndex: "journal", filterKey: "journal", filterType: "select", selectOptions: JOURNAL_OPTIONS },
  { title: "Cleared By", dataIndex: "updatedBy", filterKey: "updatedBy", filterType: "input" },
  { title: "Remark", dataIndex: "remark", filterKey: "remark", filterType: "input", width: 200 },
];

export default function ReconciledTab({ year, refreshKey, onLoadingChange, clearType }) {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({ current: 1, pageSize: 25, total: 0 });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [unreconcileLoading, setUnreconcileLoading] = useState(false);
  const requestIdRef = useRef(0);

  const typeText = clearType === 1 ? "manual" : "auto";

  const clearSelection = () => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
  };

  const load = async (page, count, nextFilters) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const params = buildRequestParams(
        { page, count, year, clearType },
        nextFilters,
        ["date1", "clearedDate"],
      );
      const res = await http.get(api.getReconciledTransByType, { params });
      if (requestId !== requestIdRef.current) return;
      if (res?.code === "1") {
        setList(res.data?.list || []);
        setPagination({
          current: res.data?.pageNum || page,
          pageSize: res.data?.pageSize || count,
          total: res.data?.total || 0,
        });
      } else {
        message.error(res?.msg);
      }
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      message.error("Failed to load data, please try again later");
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    setFilters({});
    clearSelection();
    load(1, pagination.pageSize, {});
  }, [year, refreshKey]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading]);

  const handleBatchUnreconcile = () => {
    if (selectedRows.length === 0) {
      message.warning("Please select at least one record");
      return;
    }
    const payload = selectedRows
      .map((row) => ({ bankId: row?.bankId, matchId: row?.matchId }))
      .filter((item) => item.bankId && item.matchId);

    if (payload.length === 0) {
      message.error("Selected records are missing bankId or matchId");
      return;
    }

    Modal.confirm({
      title: "Confirm",
      content: `Are you sure you want to cancel ${typeText} reconciliation for the ${payload.length} selected record(s)?`,
      centered: true,
      okText: "Confirm",
      cancelText: "Cancel",
      onOk: async () => {
        setUnreconcileLoading(true);
        try {
          const res = await http.post(api.getBatchUnreconciledTrans, payload);
          if (res?.code === "1") {
            message.success("Operation successful");
            clearSelection();
            load(pagination.current, pagination.pageSize, filters);
          } else {
            message.error(res?.msg || "Operation failed");
          }
        } catch (e) {
          message.error("Operation failed, please try again later");
        } finally {
          setUnreconcileLoading(false);
        }
      },
    });
  };

  const handleTableChange = (p, tableFilters) => {
    const merged = { ...filters };
    Object.keys(tableFilters || {}).forEach((k) => {
      if (tableFilters[k]?.length > 0) merged[k] = tableFilters[k][0];
    });
    load(p.current || 1, p.pageSize || pagination.pageSize, merged);
  };

  return (
    <>
      <div style={{ marginBottom: 12, display: "flex", justifyContent: "flex-end" }}>
        <Button
          type="primary"
          loading={unreconcileLoading}
          disabled={selectedRowKeys.length === 0}
          onClick={handleBatchUnreconcile}
        >
          {clearType === 1 ? "Cancel Manual Reconcile" : "Cancel Auto Reconcile"}
          {selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ""}
        </Button>
      </div>

      <EnhancedTable
        columns={columns}
        dataSource={list}
        loading={loading}
        rowKey={(r) => r?.bankTransNewId ?? r?.id}
        filters={filters}
        onFilterChange={(k, v) => setFilters((prev) => ({ ...prev, [k]: v }))}
        onFilterSubmit={(f) => {
          setFilters(f);
          load(1, pagination.pageSize, f);
        }}
        onChange={handleTableChange}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions: ["10", "25", "50", "100"],
          showTotal: (t) => `Total ${t} items`,
        }}
        rowSelection={{
          selectedRowKeys,
          preserveSelectedRowKeys: false,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          },
        }}
        scroll={{ y: "calc(100vh - 320px)" }}
      />
    </>
  );
}